'use client';

import { c as _c } from "react/compiler-runtime";
import { Alert, Stack, Typography } from '@mui/material';
import type { SqlSetupCheckResult } from '@repo/api-gw-sdk';
import { SqlSetupCheckStatus } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { download } from '@/components/buttons/downloadButton';
import { MainDialog } from '@/components/dialogs/mainDialog';
import { CopyableCode } from '@/components/shared/copyableCode';
import { TemporalWorkflowLink } from '@/components/shared/temporalWorkflowLink';
import { useDAL } from '@/contexts/useDAL';
import { useIsDevModeEnabled } from '@/contexts/useIsDevModeEnabled';
import { useWorkspace } from '@/contexts/useWorkspace';
import { useTemporalWorkflowUrl } from '@/utils/temporalLink';
import { CheckingIndicator, stepFooterButtons, type StepDialogFadeProps } from './stepCheckControls';
import { SQL_STEP_DESCRIPTION } from '../messages';
import { useRdsCdcStatus } from '../useRdsCdcStatus';

// setupScript is rendered from cdc_setup.sh — a shell wrapper around the cluster and per-database SQL,
// so the name has to keep the .sh extension for the downloaded file to be runnable.
const SETUP_SCRIPT_FILENAME = 'cdc_setup.sh';

// The verdict endpoint reads a cached value and answers in milliseconds, so it can be polled far
// tighter than check-cdc-prerequisites, whose live AWS checks take seconds per call.
const VERDICT_POLL_INTERVAL_MS = 1500;

// One conversion for every timestamp comparison in this file, so a never-run result (absent or
// zero-date checkedAt) maps to the same number on both sides of a baseline check.
const checkedAtMs = (check: SqlSetupCheckResult | null | undefined) => check?.checkedAt ? new Date(check.checkedAt).getTime() : 0;

// Watchdog for a run whose verdict never registers (e.g. the workflow dies before persisting any
// status): without a bound the dialog would verify and poll forever. A local duration, so browser
// clock skew cannot affect it. A healthy run reports within seconds.
const VERIFY_WATCHDOG_MS = 120_000;

// The stock Alert keeps the icon 2px below the first line of text: its 22px icon box and the 16px
// message line each carry their own vertical padding. Sound for a one-line message only, so
// MissingObjects keeps the default and holds its icon at the top of the list.
const ONE_LINE_ALERT_SX = {
  alignItems: 'center',
  '& .MuiAlert-icon': {
    padding: '0'
  }
};
const MissingObjects = t0 => {
  const $ = _c(6);
  const {
    missingObjects
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Typography variant="body2">These CDC objects are missing from the database. Re-run the script above, then check again.</Typography>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      marginTop: "8px",
      paddingLeft: "24px"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== missingObjects) {
    t3 = missingObjects.map(_temp);
    $[2] = missingObjects;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <Alert severity="error">{t1}<Stack component="ul" sx={t2}>{t3}</Stack></Alert>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
export const SqlCommandsDialog = ({
  resourceId,
  onBack,
  onClose,
  ...fadeProps
}: {
  resourceId: string;
  onBack: () => void;
  onClose: () => void;
} & StepDialogFadeProps) => {
  const dal = useDAL();
  const {
    snackbar
  } = useWorkspace();
  const [isStarting, setIsStarting] = useState(false);
  // Server stamp of whatever verdict existed at the click (null = no run live). This run's verdict is
  // any non-IN_PROGRESS result stamped differently: the workflow persists IN_PROGRESS a moment after
  // the request returns, so status alone would end the run before it even registers, and comparing
  // server timestamps to each other keeps browser clock skew out of the decision entirely.
  const [runBaselineMs, setRunBaselineMs] = useState<number | null>(null);
  const [verificationWorkflow, setVerificationWorkflow] = useState<{
    id: string;
    namespace: string;
  } | null>(null);
  const isDevMode = useIsDevModeEnabled();
  const temporalWorkflowUrl = useTemporalWorkflowUrl();
  const {
    data,
    isLoading
  } = dal.inventory.privateLink.useOnboardRdsCdc(resourceId);
  const {
    refresh: refreshPrerequisites,
    isPrivateLinkActive
  } = useRdsCdcStatus(resourceId);
  // isVerdictLoading gates the check buttons: starting a run computes its baseline from the loaded
  // verdict, and a click before the first fetch settles would misread the previous run's stored
  // result, once it loads, as this run's outcome.
  const {
    data: verdictData,
    isLoading: isVerdictLoading,
    mutate: refreshVerdict
  } = dal.inventory.privateLink.useCdcSqlSetupCheck(resourceId, {
    refreshInterval: latest => {
      const check = latest?.sqlSetupCheck;
      const isRunning = check?.status === SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_IN_PROGRESS;
      // Keep polling from the click until a verdict stamped differently from the baseline arrives:
      // the stored status alone would stop the poll before the run it belongs to even registers.
      const awaitingThisRun = runBaselineMs !== null && checkedAtMs(check) === runBaselineMs;
      return isRunning || awaitingThisRun ? VERDICT_POLL_INTERVAL_MS : 0;
    }
  });
  const setupScript = data?.sqlInstructions?.setupScript ?? '';
  const sqlSetupCheck = verdictData?.sqlSetupCheck;
  const isServerVerifying = sqlSetupCheck?.status === SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_IN_PROGRESS;

  // The footer follows the last terminal verdict rather than the live status: a re-verification
  // flips the status to IN_PROGRESS, and deriving from it directly makes "Verify again" vanish and
  // the primary button change text mid-run.
  const [lastTerminalStatus, setLastTerminalStatus] = useState<SqlSetupCheckStatus | undefined>(undefined);
  useEffect(() => {
    const status = sqlSetupCheck?.status;
    if (status && status !== SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_IN_PROGRESS) {
      setLastTerminalStatus(status);
    }
  }, [sqlSetupCheck?.status]);
  const terminalStatus = isServerVerifying ? lastTerminalStatus : sqlSetupCheck?.status ?? lastTerminalStatus;
  const isSqlStepDone = terminalStatus === SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_OK;
  const isSqlStepFailed = terminalStatus === SqlSetupCheckStatus.SQL_SETUP_CHECK_STATUS_FAILED;
  // A verdict still carrying the baseline stamp belongs to a previous run, so it must not end this
  // one's spinner.
  const hasVerdictForThisRun = runBaselineMs !== null && !isServerVerifying && checkedAtMs(sqlSetupCheck) !== runBaselineMs;
  const isVerifying = runBaselineMs !== null ? !hasVerdictForThisRun : isServerVerifying;
  const missingObjects = sqlSetupCheck?.missingObjects ?? [];

  // The hub and the Backup details row read the heavy prerequisites key. One refresh when the verdict
  // lands keeps them current without tight-polling seconds of AWS checks.
  useEffect(() => {
    if (hasVerdictForThisRun) {
      void refreshPrerequisites();
    }
  }, [hasVerdictForThisRun, refreshPrerequisites]);

  // Only the window before the run registers server-side: once IN_PROGRESS is stored, the server's
  // own bounds (run timeout, TTL) end the run, and a slow-but-live run must not be cut off here.
  const isRunAwaitingVerdict = runBaselineMs !== null && isVerifying && !isServerVerifying;
  useEffect(() => {
    if (!isRunAwaitingVerdict) {
      return;
    }
    const watchdog = setTimeout(() => {
      setRunBaselineMs(null);
      snackbar.showErrorMessage('The verification did not report back. Try again, or contact support if it keeps happening.');
    }, VERIFY_WATCHDOG_MS);
    return () => clearTimeout(watchdog);
  }, [isRunAwaitingVerdict, snackbar]);

  // Verification is asynchronous: this starts it and the verdict poll picks the result up. Marking the
  // run live before the request rather than after it is what makes the spinner appear on the click
  // instead of one round trip later.
  const checkAndContinue = async () => {
    setIsStarting(true);
    setRunBaselineMs(checkedAtMs(sqlSetupCheck));
    try {
      const {
        workflowId,
        workflowNamespace
      } = await dal.inventory.privateLink.verifyRdsCdcSqlSetup(resourceId);
      setVerificationWorkflow(workflowId && workflowNamespace ? {
        id: workflowId,
        namespace: workflowNamespace
      } : null);
      await refreshVerdict();
    } catch (error) {
      setRunBaselineMs(null);
      setVerificationWorkflow(null);
      snackbar.showErrorMessage(await extractErrorMessage(error));
    } finally {
      setIsStarting(false);
    }
  };
  return <MainDialog isLoading={isLoading} scrollableBody {...fadeProps} header={{
    title: 'Run SQL commands on the database',
    subtitle: SQL_STEP_DESCRIPTION,
    onBack,
    onClose
  }} footer={{
    ...stepFooterButtons({
      isDone: isSqlStepDone,
      isBusy: isStarting || isVerifying || isVerdictLoading,
      // The script itself stays readable and downloadable without PrivateLink; only verifying it
      // needs the connection, so the blocker sits on the button rather than on the whole step.
      blockedReason: isPrivateLinkActive ? undefined : 'Available once AWS PrivateLink is active',
      testIdPrefix: 'rds-cdc-sql',
      onContinue: onBack,
      onCheckAndContinue: () => void checkAndContinue(),
      onVerifyAgain: () => void checkAndContinue()
    }),
    // The verification workflow is only identified by a run started from this dialog, so before one
    // the link stays visible but disabled rather than disappearing.
    children: isDevMode && <TemporalWorkflowLink unavailableReason='Developer mode: Run verification first to see Workflow execution' href={verificationWorkflow && temporalWorkflowUrl(verificationWorkflow.namespace, verificationWorkflow.id)} />
  }}>
      <Stack sx={{
      gap: '16px',
      paddingX: '40px'
    }}>
        {!!setupScript && <>
            <Alert severity='info' sx={ONE_LINE_ALERT_SX}>
              This script must run with superuser privileges.
            </Alert>
            <CopyableCode code={setupScript} title={SETUP_SCRIPT_FILENAME} tooltipTitle='Creates the eon_replicator role, the eon_publication publication, and the event triggers Eon uses to capture DDL changes.' codeClassName='overflow-y-auto max-h-[40vh]' />
            <ButtonWithIcon variant='outlined' icon='material-symbols-download-rounded' iconPosition='right' text='Download Setup Script' onClick={() => download(setupScript, SETUP_SCRIPT_FILENAME, {
          type: 'text/x-shellscript'
        })} sx={{
          alignSelf: 'flex-start'
        }} />
          </>}

        {isVerifying && <CheckingIndicator text='Verifying the setup on your database. This might take a few minutes.' />}

        {!isVerifying && isSqlStepDone && <Alert severity='success' sx={ONE_LINE_ALERT_SX}>
            All required CDC objects are present on the database.
          </Alert>}

        {!isVerifying && isSqlStepFailed && <Alert severity='error'>
            {sqlSetupCheck?.error || 'Verification failed. Try again, or contact support if it keeps failing.'}
          </Alert>}

        {!isVerifying && missingObjects.length > 0 && <MissingObjects missingObjects={missingObjects} />}
      </Stack>
    </MainDialog>;
};
function _temp(missingObject) {
  return <Typography component="li" variant="body2" key={missingObject}><code>{missingObject}</code></Typography>;
}